import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnInit,
  signal,
  ChangeDetectorRef,
  DestroyRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserMgmtFacade } from '../../../user-management/facade/user-mgmt.facade';
import { formatBytes } from '../../../../shared/utils/dataset-utils';
import { FontAwesomeSharedModule } from '../../../../shared/font-awesome/font-awesome';
import { ProjectFacade } from '../../../projects/facades/project.facade';
import { RequirementHintComponent } from '../../../../shared/components/requirement-hint/requirement-hint.component';
import { MetadataSchemaService, SchemaCacheKey } from '../../../../shared/services/metadata-schema.service';
import {
  ICrossFieldRuleSpec,
  IMetadataFieldSchema,
  IMetadataSchema,
} from '../../../../shared/models/MetadataSchema';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
  Validators,
  FormArray,
  AbstractControl,
} from '@angular/forms';
import { ToastService } from '../../../../shared/services/toast.service';
import { LoggerService } from '../../../../shared/services/logger.service';
import { SequenceMetadataFacade } from '../../facade/sequence-metadata.facade';
import { LabFacade } from '../../facade/lab.facade';
import { isLabReadOnly } from '../../../../shared/utils/lab-status';
import { MetadataMgmtFacade } from '../../../metadata-mgmt/facade/metadata-mgmt.facade';
import { UploadsService } from '../../services/uploads.service';
import { IMetadataSourceType, IMetadataKeyList } from '../../../metadata-mgmt/models/Metadata-mgmt';
import { ISequenceMetadata, KeyValueItem, MetadataTag, MetadataTagCorrection } from '../../models/SequenceMetadata';
import { CopiedToDataset, ISequenceFile } from '../../models/Uploads';
import { canEditDraft, labDraftAccess } from '../../utils/draft-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LabSequenceDrawerState } from './lab-sequence-drawer-state';
import { NgSelectModule } from '@ng-select/ng-select';
import { SuggestOptionModalComponent } from '../../../../shared/components/suggest-option-modal/suggest-option-modal.component';
import { PendingOptionRequestsStore } from '../../../../shared/services/pending-option-requests.store';
import { BatchCsvUploadState } from '../../modals/batch-csv-upload/batch-csv-upload-state.service';
import { DialogService } from '../../../../shared/services/dialog.service';
import { Actions, ofType } from '@ngrx/effects';
import { SequenceMetadataApiActions } from '../../state/actions';
import { IAnalyticalDatasetFile } from '../../../datasets/models/AnalyticalDataset';
import { Router, RouterLink } from '@angular/router';
import {
  UnsavedChangesConfirmationModalComponent,
  UNSAVED_CHANGES_MODAL_ID,
} from '../../../../shared/modals/unsaved-changes-confirmation/unsaved-changes-confirmation-modal.component';
import { PostAnalysisSectionComponent } from '../post-analysis-section/post-analysis-section.component';

// GPS coordinate format: decimal degrees, per NCBI/SRA specification.
// Format: "d[d.dddd] N|S d[dd.dddd] E|W" — at least 4 decimal places required.
// Accepts: "38.9800 N 77.1100 W" or "33.4230 N, 111.9278 W"
const GPS_PATTERN = /^\d{1,2}\.\d{4,}\s+[NS],?\s*\d{1,3}\.\d{4,}\s+[EW]$/i;

// Shape of a single issue returned by the metadata validation engine on the
// backend (see asu_apgap/metadata_validations/validators/schemas.py::Issue).
export interface PromotionValidationIssue {
  severity: 'ERROR' | 'WARNING';
  code: string;
  message: string;
  template_keys: string[];
  rule_kind?: string;
  rule_id?: number | null;
  issue_hash?: string;
}

// Shape of the 400 response body returned by /files/<id>/set-primary/ when
// promotion is blocked. Mirrors the payload built in `FileViewSet.set_primary`.
interface PromotionErrorBody {
  error?: string;
  code?: 'VALIDATION_ERRORS' | 'UNACKNOWLEDGED_WARNINGS' | 'MISSING_REQUIRED_METADATA' | 'CANNOT_PROMOTE' | string;
  detail?: string;
  missing_metadata_keys?: string[];
  missing_count?: number;
  errors?: PromotionValidationIssue[];
  warnings?: PromotionValidationIssue[];
  unacknowledged_warning_hashes?: string[];
  current_status?: string;
  gcs_file_path?: string[];
}

@Component({
  selector: 'app-lab-sequence-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeSharedModule,
    ReactiveFormsModule,
    FormsModule,
    NgSelectModule,
    UnsavedChangesConfirmationModalComponent,
    RequirementHintComponent,
    SuggestOptionModalComponent,
    RouterLink,
    PostAnalysisSectionComponent,
  ],
  templateUrl: './lab-sequence-drawer.component.html',
})
export class LabSequenceDrawerComponent implements OnInit {
  private readonly userMgmtFacade = inject(UserMgmtFacade);
  private readonly projectFacade = inject(ProjectFacade);
  private readonly fb = inject(FormBuilder);
  private readonly toastService = inject(ToastService);
  private readonly logger = inject(LoggerService);
  private readonly dialogService = inject(DialogService);
  private readonly sequenceMetadataFacade = inject(SequenceMetadataFacade);
  private readonly metadataMgmtFacade = inject(MetadataMgmtFacade);
  private readonly metadataSchemaService = inject(MetadataSchemaService);
  private readonly drawerState = inject(LabSequenceDrawerState);
  private readonly labFacade = inject(LabFacade);
  private readonly batchCsvUploadState = inject(BatchCsvUploadState);
  private readonly uploadsService = inject(UploadsService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  formatBytes = formatBytes;

  meUser = this.userMgmtFacade.getMeUserSignal;
  // Use injectable service signals instead of deprecated global signals
  selectedSequence = this.drawerState.selectedSequence;
  analyticalDataset = this.drawerState.analyticalDataset;
  postAnalysisReadOnly = this.drawerState.postAnalysisReadOnly;
  isDrawerOpen = this.drawerState.isOpen;
  currentPage = this.drawerState.currentPage;
  pageSize = this.drawerState.pageSize;
  totalItems = this.drawerState.totalItems;

  // Drawer state for pagination
  drawerStateIsOpen = this.drawerState.isOpen;
  drawerCurrentIndex = this.drawerState.currentIndexSignal;
  drawerAllSequenceIds = this.drawerState.allSequenceIds;

  // Show pagination arrows only when the drawer was opened over an analytical
  // dataset's sequence list (multiple navigable items).
  shouldShowPagination = computed(() => {
    const hasMultipleSequences = this.drawerAllSequenceIds().length > 1;
    const isAnalyticalDatasetContext = this.analyticalDataset() !== null;
    return hasMultipleSequences && isAnalyticalDatasetContext;
  });

  // File id + source type the embedded post-analysis section binds to.
  // In a dataset context this is the copy's id; the backend resolves it to the
  // original so the published history still shows (editing stays disabled).
  postAnalysisFileId = computed<number | null>(() => {
    const dataset = this.analyticalDataset();
    if (dataset) return dataset.id ?? null;
    return this.selectedSequence()?.id ?? null;
  });
  postAnalysisSourceTypeId = computed<number | null>(() => {
    const dataset = this.analyticalDataset();
    if (dataset) return dataset.source_type ?? null;
    const raw = this.selectedSequence()?.source_type;
    return typeof raw === 'number' ? raw : null;
  });

  showAlert = signal(true);
  alertMessage = this.drawerState.alertMessage;
  preparingCorrection = this.batchCsvUploadState.preparingCorrection;

  // Validation issues returned by the backend when trying to promote a sequence
  // file to PRIMARY. Used for the summary list in the alert banner; individual
  // issues are also pinned onto matching form controls as `serverError`.
  promotionIssues = signal<{
    errors: PromotionValidationIssue[];
    warnings: PromotionValidationIssue[];
    missingRequiredKeys: string[];
  } | null>(null);

  // Collapse/Expand state for sections
  isCopiedToExpanded = signal(true);
  isSequenceDetailsExpanded = signal(true);
  isSourceTypeExpanded = signal(true);

  // Source types and metadata keys
  sourceTypes: IMetadataSourceType[] = [];
  sourceTypeMetadataKeys: IMetadataKeyList[] = [];
  coreMetadataKeys: IMetadataKeyList[] = [];
  selectedSourceTypeId: number | null = null;

  // Metadata schema (templates + validator rules + cross-field rules) used to
  // render requirement hints next to each field and to enforce backend rules
  // client-side. Loaded lazily per source type and cached in the service.
  private schemaByName = new Map<string, IMetadataFieldSchema>();
  // Cross-field rules pulled out of the schema for the current source type so
  // the per-field hint can flag the ones attached to it as the right-hand side.
  crossFieldRules: ICrossFieldRuleSpec[] = [];
  private isUpdatingSourceTypeProgrammatically = false;
  organization: string = '';
  // Sequence Metadata Form - only core fields
  sequenceMetadataForm: FormGroup = this.fb.group({
    source_type: [null],
    core_fields: this.fb.group({}),
    sourceType_fields: this.fb.group({}),
    description: [''],
    gcs_file_path: ['', Validators.required],
  });

  sequenceMetadata: ISequenceMetadata | null = null;
  metadataLoading = this.sequenceMetadataFacade.loadingSignal;

  // Form sections array - only updated when keys actually change
  formSections: Array<{
    title: string;
    form: FormGroup;
    sectionKey: 'core_fields' | 'sourceType_fields';
    keys: IMetadataKeyList[];
  }> = [];

  // Track initial form state to detect unsaved changes
  private initialFormValue: Record<string, unknown> | null = null;
  private pendingClose = false;

  // Last-seen correction counter, so the reload effect only fires on new
  // corrections
  private lastCorrectionSaved = 0;

  // Signal to force form re-render
  formReady = signal(false);

  // Suggest-option modal coordination
  @ViewChild(SuggestOptionModalComponent) suggestModal?: SuggestOptionModalComponent;
  suggestModalFileIds: number[] = [];
  suggestModalTemplateId: number | null = null;
  suggestModalKeyName = '';

  openSuggestOption(key: IMetadataKeyList): void {
    const file = this.selectedSequence();
    if (!file?.id) return;
    this.suggestModalFileIds = [file.id];
    this.suggestModalTemplateId = key.id;
    this.suggestModalKeyName = key.key_name || key.name;
    setTimeout(() => this.suggestModal?.open(), 0);
  }

  readonly pendingStore = inject(PendingOptionRequestsStore);

  pendingValuesForKey(key: IMetadataKeyList): string[] {
    const fileId = this.selectedSequence()?.id;
    if (!fileId) return [];
    return this.pendingStore.pendingValues(fileId, key.id);
  }

  constructor() {}

  ngOnInit(): void {
    // Initialize selectedSourceTypeId from the selected sequence FIRST
    const selectedSeq = this.selectedSequence();
    if (selectedSeq && selectedSeq.source_type) {
      this.selectedSourceTypeId = Number(selectedSeq.source_type);
    }

    this.drawerState.setSourceTypeId(this.selectedSourceTypeId ? this.selectedSourceTypeId : 0);
    this.metadataMgmtFacade.getSourceTypes();

    this.lastCorrectionSaved = this.drawerState.correctionSaved();

    this.formReady.set(false);

    // Reset form with the correct source type value
    this.sequenceMetadataForm.reset({
      source_type: this.selectedSourceTypeId || null,
      description: selectedSeq?.description || '',
      gcs_file_path: selectedSeq?.gcs_file_path || '',
      core_fields: {},
      sourceType_fields: {},
    });
    this.sequenceMetadataForm.markAsPristine();
    this.sequenceMetadataForm.markAsUntouched();

    setTimeout(() => {
      this.formReady.set(true);
      this.cdr.detectChanges();
    }, 0);

    // Clear server error on gcs_file_path when user starts typing
    this.sequenceMetadataForm
      .get('gcs_file_path')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const control = this.sequenceMetadataForm.get('gcs_file_path');
        if (control?.errors?.['serverError']) {
          // Remove only the serverError, keep other validators
          const updatedErrors = { ...control.errors };
          delete updatedErrors['serverError'];
          control.setErrors(Object.keys(updatedErrors).length > 0 ? updatedErrors : null);
        }
      });

    // Track when form becomes dirty
    this.sequenceMetadataForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.sequenceMetadataForm.dirty && !this.initialFormValue) {
        // Get all dirty field names
        const dirtyFields: string[] = [];
        Object.keys(this.sequenceMetadataForm.controls).forEach((key) => {
          const control = this.sequenceMetadataForm.get(key);
          if (control?.dirty) {
            if (control instanceof FormGroup) {
              // For nested form groups, get the dirty fields within
              Object.keys(control.controls).forEach((nestedKey) => {
                if (control.get(nestedKey)?.dirty) {
                  dirtyFields.push(`${key}.${nestedKey}`);
                }
              });
            } else {
              dirtyFields.push(key);
            }
          }
        });
        dirtyFields.forEach((field) => this.logger.debug(`Form field dirty: ${field}`, 'LabSequenceDrawerComponent'));
        this.initialFormValue = this.sequenceMetadataForm.value;
      }
    });

    // Fetch sequence metadata if we have a selected sequence
    if (selectedSeq && selectedSeq.id) {
      this.sequenceMetadataFacade.getSequenceMetadata(selectedSeq.id);
      this.pendingStore.loadForFiles([selectedSeq.id]);
    }

    // Watch drawer state changes to update selected sequence when navigating
    // Use injector option to ensure effect() is called in an injection context
    effect(
      () => {
        const sequenceFromList = this.drawerState.selectedSequenceId();
        if (sequenceFromList?.id && this.drawerState.isOpen()) {
          // Use the sequence from the list (which includes source_type from FileListSerializer)
          // instead of fetching from detail endpoint (which uses FileDetailSerializer without source_type)

          if (this.analyticalDataset()) {
            this.drawerState.setAnalyticalDataset(sequenceFromList as IAnalyticalDatasetFile);
            return;
          }

          // Set the selected sequence directly from the list
          const sequenceFile = sequenceFromList as ISequenceFile;
          this.drawerState.setSelectedSequence(sequenceFile);

          // Pull pending option requests for the badge UI
          if (sequenceFile?.id) this.pendingStore.loadForFiles([sequenceFile.id]);

          // Load file description and populate the form
          const description = sequenceFile?.description || '';
          const descriptionControl = this.sequenceMetadataForm.get('description');
          if (descriptionControl) {
            descriptionControl.setValue(description || '');
          }

          // Load file path and populate the form
          const gcsFilePath = sequenceFile?.gcs_file_path || '';
          const gcsFilePathControl = this.sequenceMetadataForm.get('gcs_file_path');
          if (gcsFilePathControl) {
            gcsFilePathControl.setValue(gcsFilePath);
          }

          // Update source type in form and component
          // Get source_type from the sequence (from list API which includes it)
          let sourceType = sequenceFile.source_type;

          // If source_type is undefined or null, try to get it from metadata tags
          if (sourceType === undefined || sourceType === null) {
            // Check if sequence file has metadata_tags with source_type
            if (sequenceFile.metadata_tags && Array.isArray(sequenceFile.metadata_tags)) {
              const tagWithSourceType = sequenceFile.metadata_tags.find(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (tag: any) => tag.source_type !== null && tag.source_type !== undefined
              );
              if (tagWithSourceType) {
                sourceType = tagWithSourceType.source_type;
              }
            }
            // If still not found, try existing metadata
            if ((sourceType === undefined || sourceType === null) && this.sequenceMetadata?.metadata_tags) {
              const tagWithSourceType = this.sequenceMetadata.metadata_tags.find(
                (tag) => tag.source_type !== null && tag.source_type !== undefined
              );
              if (tagWithSourceType) {
                sourceType = tagWithSourceType.source_type;
              }
            }
          }

          this.selectedSourceTypeId = sourceType !== undefined && sourceType !== null ? Number(sourceType) : null;

          // Update the form control (set flag to prevent change event handler from running)
          this.isUpdatingSourceTypeProgrammatically = true;
          const sourceTypeControl = this.sequenceMetadataForm.get('source_type');
          if (sourceTypeControl) {
            const sourceTypeValue = this.selectedSourceTypeId ? Number(this.selectedSourceTypeId) : '';
            sourceTypeControl.setValue(sourceTypeValue, { emitEvent: false });
          }
          setTimeout(() => {
            this.isUpdatingSourceTypeProgrammatically = false;
          }, 0);

          // Ensure source types are loaded
          if (this.sourceTypes.length === 0) {
            this.metadataMgmtFacade.getSourceTypes();
          }

          // Load metadata keys for the source type if it exists
          if (this.selectedSourceTypeId !== null) {
            this.loadMetadataKeys(this.selectedSourceTypeId);
          } else {
            // Clear source type fields if no source type
            const sourceTypeFieldsGroup = this.sequenceMetadataForm.get('sourceType_fields') as FormGroup;
            if (sourceTypeFieldsGroup) {
              Object.keys(sourceTypeFieldsGroup.controls).forEach((key) => {
                sourceTypeFieldsGroup.removeControl(key);
              });
            }
            this.sourceTypeMetadataKeys = [];
            this.updateFormSections();
          }

          // Fetch metadata - the subscription will handle checking and showing alert
          if (sequenceFile.id > 0) {
            this.sequenceMetadataFacade.getSequenceMetadata(sequenceFile.id);
          }
        }
      },
      { injector: this.injector }
    );

    // Reload the open sequence after a metadata correction saves in the bulk
    // editor
    effect(
      () => {
        const saved = this.drawerState.correctionSaved();
        if (saved === this.lastCorrectionSaved) return;
        this.lastCorrectionSaved = saved;
        if (this.drawerState.isOpen() && this.selectedSequence()) {
          this.reloadSelectedSequence();
        }
      },
      { injector: this.injector }
    );

    // REMOVED: This effect was causing stale description values to persist
    // The description is already being set in ngOnInit from the API response
    // and this effect was overriding it with potentially stale values from the signal
    // effect(
    //   () => {
    //     const sequence = this.selectedSequence();
    //     if (!sequence) return;
    //
    //     // Handle source_type
    //     const sourceTypeControl = this.sequenceMetadataForm.get('source_type');
    //     if (sequence.source_type && sourceTypeControl) {
    //       sourceTypeControl.setValue(Number(sequence.source_type), { emitEvent: false });
    //       this.selectedSourceTypeId = Number(sequence.source_type);
    //     }
    //
    //     // Handle description
    //     const descriptionControl = this.sequenceMetadataForm.get('description');
    //     if (sequence.description && descriptionControl) {
    //       descriptionControl.setValue(sequence.description, { emitEvent: false });
    //     }
    //   },
    //   { injector: this.injector }
    // );

    this.metadataMgmtFacade.sourceTypes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((types) => {
      this.sourceTypes = Array.isArray(types) ? types : [];
      if (this.sourceTypes.length === 0) {
        return;
      }
      this.loadMetadataKeys();
      // If source type is selected, load its keys too and update form control
      if (this.selectedSourceTypeId !== null) {
        this.loadMetadataKeys(this.selectedSourceTypeId);
        // Update the form control with the selected source type (ensure it's set as string)
        this.isUpdatingSourceTypeProgrammatically = true;
        const sourceTypeControl = this.sequenceMetadataForm.get('source_type');
        if (sourceTypeControl) {
          const sourceTypeValue = Number(this.selectedSourceTypeId);
          sourceTypeControl.setValue(sourceTypeValue, { emitEvent: false });
        }
        setTimeout(() => {
          this.isUpdatingSourceTypeProgrammatically = false;
        }, 0);
      }
    });

    this.sequenceMetadataFacade.selectedSequenceMetadata$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((metadata) => {
        if (!metadata) return;

        this.sequenceMetadata = metadata;
        this.rebuildFormGroups();

        // If we don't have a source type yet, try to get it from metadata tags
        if (this.selectedSourceTypeId === null && metadata.metadata_tags) {
          const tagWithSourceType = metadata.metadata_tags.find(
            (tag) => tag.source_type !== null && tag.source_type !== undefined
          );
          if (tagWithSourceType && tagWithSourceType.source_type !== null) {
            this.selectedSourceTypeId = Number(tagWithSourceType.source_type);

            // Update the form control
            this.isUpdatingSourceTypeProgrammatically = true;
            const sourceTypeControl = this.sequenceMetadataForm.get('source_type');
            if (sourceTypeControl) {
              sourceTypeControl.setValue(Number(this.selectedSourceTypeId), { emitEvent: false });
            }
            setTimeout(() => {
              this.isUpdatingSourceTypeProgrammatically = false;
            }, 0);
          }
        }

        // Ensure core metadata keys are loaded before trying to populate form fields
        if (this.coreMetadataKeys.length === 0) {
          this.loadMetadataKeys();
        }

        // Repopulate form fields when metadata tags are updated
        // This ensures the form is updated when metadata is fetched after CSV upload
        if (this.coreMetadataKeys.length > 0) {
          this.makeCoreFieldsFromTags();
        }
        if (this.sourceTypeMetadataKeys.length > 0) {
          this.makeSourceTypeFieldsFromTags();
        }

        // Always capture initial form value after metadata is loaded
        // This ensures we have a baseline even if there are no metadata tags
        this.captureInitialFormValue();
      });

    this.userMgmtFacade.getMeUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (user?.organization) {
        this.organization = user.organization.display_name || '';
      }
    });

    // Subscribe to metadata keys
    this.metadataMgmtFacade.metadataKeys$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((keys) => {
      const metadataKeys = Array.isArray(keys) ? keys : [];
      if (metadataKeys.length === 0) return;

      // Check if these are core keys (source_type is null) or source-type specific keys
      const allCoreKeys = metadataKeys.every((k) => k.source_type == null);
      const allSourceTypeKeys = metadataKeys.every((k) => k.source_type != null);

      if (allCoreKeys) {
        // Build core fields
        const coreGroup = this.sequenceMetadataForm.get('core_fields') as FormGroup;
        const sortedKeys = [...keys].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        sortedKeys.forEach((key) => {
          const validators = this.getValidatorsForKey(key);
          // Use key_name to match with metadata tag keys
          const controlName = key.key_name || key.name;
          // Check if it's a multi-value field (MULTISELECT, SELECT+multiple_select, or LIST)
          const isMultiValue =
            key.data_type === 'MULTISELECT' ||
            key.data_type === 'LIST' ||
            (key.data_type === 'SELECT' && key.multiple_select);

          if (isMultiValue) {
            coreGroup.addControl(controlName, this.fb.array([], validators));
          } else {
            coreGroup.addControl(controlName, this.fb.control(null, validators));
          }
        });
        this.coreMetadataKeys = keys;

        this.updateFormSections();
        // Always call makeCoreFieldsFromTags when core keys are loaded and we have metadata tags
        // This ensures form fields are populated even if metadata arrived before keys
        if (this.sequenceMetadata?.metadata_tags?.length) {
          this.makeCoreFieldsFromTags();
        }
        // Pull in the schema (templates + rules) for hint rendering + live
        // validation. Safe to call repeatedly; service caches per source type.
        this.applySchemaToForm();
      } else if (allSourceTypeKeys) {
        const sourceTypeGroup = this.sequenceMetadataForm.get('sourceType_fields') as FormGroup;
        Object.keys(sourceTypeGroup.controls).forEach((k) => sourceTypeGroup.removeControl(k));
        const sortedKeys = [...keys].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        sortedKeys.forEach((key) => {
          const validators = this.getValidatorsForKey(key);
          // Use key_name to match with metadata tag keys
          const controlName = key.key_name || key.name;
          // Check if it's a multi-value field (MULTISELECT, SELECT+multiple_select, or LIST)
          const isMultiValue =
            key.data_type === 'MULTISELECT' ||
            key.data_type === 'LIST' ||
            (key.data_type === 'SELECT' && key.multiple_select);

          if (isMultiValue) {
            sourceTypeGroup.addControl(controlName, this.fb.array([], validators));
          } else {
            sourceTypeGroup.addControl(controlName, this.fb.control(null, validators));
          }
        });
        this.sourceTypeMetadataKeys = keys;
        this.updateFormSections();
        this.makeSourceTypeFieldsFromTags();
        this.applySchemaToForm();
      }
    });

    // Handle errors to highlight filename field if there's a gcs_file_path error
    this.actions$
      .pipe(
        ofType(
          SequenceMetadataApiActions.CreateSequenceMetadataFailure,
          SequenceMetadataApiActions.PromoteToPrimaryFailure
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ errors }) => {
        // Check if the error is related to gcs_file_path (duplicate filename)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorObj = errors as any;
        const body: PromotionErrorBody = errorObj?.error ?? {};

        // Check for gcs_file_path array error format
        const gcsFilePathArrayError = body.gcs_file_path?.[0];
        // Check for "Filename already in use" error in detail field
        const detailError = body.detail;
        const isFilenameError = detailError?.toLowerCase?.()?.includes('filename');

        const filenameErrorMessage = gcsFilePathArrayError || (isFilenameError ? detailError : null);

        if (filenameErrorMessage) {
          const gcsFilePathControl = this.sequenceMetadataForm.get('gcs_file_path');
          if (gcsFilePathControl) {
            gcsFilePathControl.setErrors({ serverError: filenameErrorMessage });
            gcsFilePathControl.markAsTouched();
          }
        }

        // Structured metadata validation errors come back from set-primary with
        // `code` VALIDATION_ERRORS / UNACKNOWLEDGED_WARNINGS / MISSING_REQUIRED_METADATA.
        // For create-metadata failures there is no validation payload; this path is a no-op.
        this.applyPromotionValidationErrors(body);
      });

    this.actions$
      .pipe(
        ofType(
          SequenceMetadataApiActions.CreateSequenceMetadataSuccess,
          SequenceMetadataApiActions.PromoteToPrimarySuccess
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const sequenceId = this.selectedSequence()?.id;
        if (!sequenceId) return;

        // Any stale promotion validation errors are now resolved.
        this.promotionIssues.set(null);

        // Reset initial form value after successful save
        this.captureInitialFormValue();

        this.uploadsService.getFileDetail(sequenceId).subscribe({
          next: (sequenceFile) => {
            if (sequenceFile && sequenceFile.id) {
              const currentSequence = this.drawerState.selectedSequence();
              this.drawerState.setSelectedSequence({
                ...sequenceFile,
                created_at: currentSequence?.created_at,
                lab_name: currentSequence?.lab_name,
                // Preserve source_type from current state since API doesn't return it
                source_type: currentSequence?.source_type ?? null,
                source_type_name: currentSequence?.source_type_name ?? null,
              });
              // Refresh the list view to show updated status
              this.drawerState.incrementRefreshSequenceList();
            }

            // If user clicked "Save & Close", close the drawer after successful save
            if (this.pendingClose) {
              this.performClose();
            }
          },
          error: (err) => this.logger.error('Error refreshing file detail', 'LabSequenceDrawerComponent', err),
        });

        this.sequenceMetadataFacade.getSequenceMetadata(sequenceId);
      });
  }

  private rebuildFormGroups(): void {
    const coreGroup = this.fb.group({});
    const sourceGroup = this.fb.group({});

    // Create fresh core controls
    this.coreMetadataKeys.forEach((key) => {
      const validators = this.getValidatorsForKey(key);
      const name = key.name; // Use backend key identifier, not display name
      const isMulti =
        key.data_type === 'MULTISELECT' ||
        key.data_type === 'LIST' ||
        (key.data_type === 'SELECT' && key.multiple_select);

      coreGroup.addControl(name, isMulti ? this.fb.array([], validators) : this.fb.control(null, validators));
    });

    // Create fresh source-type controls
    this.sourceTypeMetadataKeys.forEach((key) => {
      const validators = this.getValidatorsForKey(key);
      const name = key.name; // Use backend key identifier, not display name
      const isMulti =
        key.data_type === 'MULTISELECT' ||
        key.data_type === 'LIST' ||
        (key.data_type === 'SELECT' && key.multiple_select);

      sourceGroup.addControl(name, isMulti ? this.fb.array([], validators) : this.fb.control(null, validators));
    });

    // Replace form groups
    this.sequenceMetadataForm.setControl('core_fields', coreGroup);
    this.sequenceMetadataForm.setControl('sourceType_fields', sourceGroup);

    this.updateFormSections();
    // Reapply schema-derived validators (cross-field + per-field) to the
    // freshly rebuilt controls. Hits the cache, no extra HTTP.
    this.applySchemaToForm();
  }

  loadMetadataKeys(selectedSourceTypeId?: number): void {
    // The drawer's main editor is the baseline (draft) workflow; post-analysis
    // fields live in the dedicated post-analysis section, so scope to BASELINE
    // to keep post-analysis templates out of this form.
    this.metadataMgmtFacade.getMetadataKeys(selectedSourceTypeId, 'BASELINE');
  }

  private updateFormSections(): void {
    this.formSections = [
      {
        title: 'Metadata',
        sectionKey: 'core_fields',
        form: this.sequenceMetadataForm.get('core_fields') as FormGroup,
        keys: this.coreMetadataKeys,
      },
      {
        title: 'Source Type',
        sectionKey: 'sourceType_fields',
        form: this.sequenceMetadataForm.get('sourceType_fields') as FormGroup,
        keys: this.sourceTypeMetadataKeys,
      },
    ];
  }

  trackById(index: number, item: IMetadataKeyList) {
    return item.id; // assuming each key has a unique id
  }

  trackBySectionTitle(index: number, section: { title: string; form: FormGroup; keys: IMetadataKeyList[] }) {
    return section.title;
  }
  makeSourceTypeFieldsFromTags(captureInitial = false): void {
    if (!this.sequenceMetadata?.metadata_tags.length) {
      return;
    }

    const sourceTypeFieldsGroup = this.sequenceMetadataForm.get('sourceType_fields') as FormGroup;

    // Group metadata_tags by key (for multi-select fields)
    const tagsByKey: { [key: string]: string[] } = {};
    this.sequenceMetadata?.metadata_tags.forEach((tag) => {
      if (tag.source_type) {
        if (!tagsByKey[tag.key]) {
          tagsByKey[tag.key] = [];
        }
        // Handle both string and array values
        if (tag.source_type === this.sequenceMetadataForm.get('source_type')?.value) {
          if (Array.isArray(tag.value)) {
            tagsByKey[tag.key].push(...tag.value);
          } else {
            tagsByKey[tag.key].push(tag.value);
          }
        }
      }
    });

    // Populate form fields
    Object.keys(tagsByKey).forEach((tagKey) => {
      // Find the metadata key definition that matches this tag key
      // Match by key_name first (since metadata tags use key_name), then fall back to name
      const keyDef = this.sourceTypeMetadataKeys.find((k) => k.key_name === tagKey || k.name === tagKey);
      if (!keyDef) {
        return;
      }

      // Get the control using the key_name (which is what the form control was created with)
      const controlName = keyDef.key_name || keyDef.name;
      const control = sourceTypeFieldsGroup.get(controlName);
      if (!control) return;

      // Check if it's a multi-value field (MULTISELECT, LIST, or SELECT+multiple_select)
      const isMultiValue =
        keyDef.data_type === 'MULTISELECT' ||
        keyDef.data_type === 'LIST' ||
        (keyDef.data_type === 'SELECT' && keyDef.multiple_select);

      if (isMultiValue) {
        // For multi-value fields, set as array
        const formArray = control as FormArray;
        formArray.clear();
        const values = tagsByKey[tagKey] || [];
        if (values.length > 0) {
          values.forEach((value) => {
            if (value) {
              formArray.push(this.fb.control(value));
            }
          });
        }
        // Update validity after populating FormArray
        formArray.updateValueAndValidity({ emitEvent: false });
      } else {
        // For single value, use first value
        let value = tagsByKey[tagKey]?.[0] || '';

        // Convert date format for DATE fields (from M/D/YYYY to YYYY-MM-DD)
        if (keyDef.data_type === 'DATE' && value) {
          value = this.convertToISODate(value);
        }

        control.setValue(value);
        control.updateValueAndValidity({ emitEvent: false });
      }
    });

    // Update form validity after populating fields
    this.sequenceMetadataForm.updateValueAndValidity({ emitEvent: false });

    // Mark form as pristine after populating from server data
    this.sequenceMetadataForm.markAsPristine();

    // Capture initial form value after populating
    if (captureInitial) {
      this.captureInitialFormValue();
    }
  }

  makeCoreFieldsFromTags(captureInitial = false): void {
    if (!this.sequenceMetadata?.metadata_tags.length) {
      return;
    }

    const coreTypeFieldsGroup = this.sequenceMetadataForm.get('core_fields') as FormGroup;

    const coreTagsKey: { [key: string]: string[] } = {};
    this.sequenceMetadata?.metadata_tags.forEach((tag) => {
      // Only process core fields (source_type is null)
      if (tag.source_type !== null) return;

      if (!coreTagsKey[tag.key]) {
        coreTagsKey[tag.key] = [];
      }
      // Handle both string and array values
      if (Array.isArray(tag.value)) {
        coreTagsKey[tag.key].push(...tag.value);
      } else {
        coreTagsKey[tag.key].push(tag.value);
      }
    });

    // Populate form fields
    Object.keys(coreTagsKey).forEach((tagKey) => {
      // Find the metadata key definition that matches this tag key
      // Match by key_name first (since metadata tags use key_name), then fall back to name
      const keyDef = this.coreMetadataKeys.find((k) => k.key_name === tagKey || k.name === tagKey);
      if (!keyDef) {
        return;
      }

      // Get the control using the key_name (which is what the form control was created with)
      const controlName = keyDef.key_name || keyDef.name;
      const control = coreTypeFieldsGroup.get(controlName);
      if (!control) {
        return;
      }

      // Check if it's a multi-value field (MULTISELECT, LIST, or SELECT+multiple_select)
      const isMultiValue =
        keyDef.data_type === 'MULTISELECT' ||
        keyDef.data_type === 'LIST' ||
        (keyDef.data_type === 'SELECT' && keyDef.multiple_select);

      if (isMultiValue) {
        // For multi-value fields, set as array
        const formArray = control as FormArray;
        formArray.clear();
        const values = coreTagsKey[tagKey] || [];
        if (values.length > 0) {
          values.forEach((value) => {
            if (value) {
              formArray.push(this.fb.control(value));
            }
          });
        }
        // Update validity after populating FormArray
        formArray.updateValueAndValidity({ emitEvent: false });
      } else {
        // For single value, use first value
        let value = coreTagsKey[tagKey]?.[0] || '';

        // Convert date format for DATE fields (from M/D/YYYY to YYYY-MM-DD)
        if (keyDef.data_type === 'DATE' && value) {
          value = this.convertToISODate(value);
        }

        control.setValue(value);
        control.updateValueAndValidity({ emitEvent: false });
      }
    });

    // Update form validity after populating fields
    this.sequenceMetadataForm.updateValueAndValidity({ emitEvent: false });

    // Mark form as pristine after populating from server data
    this.sequenceMetadataForm.markAsPristine();

    // Capture initial form value after populating
    if (captureInitial) {
      this.captureInitialFormValue();
    }
  }

  private captureInitialFormValue() {
    // Capture the current form value as the initial state
    // Use a timeout to ensure all form updates are complete
    setTimeout(() => {
      this.initialFormValue = JSON.parse(JSON.stringify(this.sequenceMetadataForm.value));
    }, 100);
  }

  getFieldControl(section: 'core_fields' | 'sourceType_fields', keyName: string): AbstractControl | null {
    return this.sequenceMetadataForm.get(`${section}.${keyName}`) || null;
  }

  getFieldArray(section: 'core_fields' | 'sourceType_fields', keyName: string): FormArray | null {
    const control = this.getFieldControl(section, keyName);
    return control instanceof FormArray ? control : null;
  }

  onSourceTypeChange(metadataSourceType: IMetadataSourceType | null): void {
    // Skip if we're programmatically updating the source type (e.g., during pagination)
    if (this.isUpdatingSourceTypeProgrammatically) {
      return;
    }

    if (!metadataSourceType?.id) {
      this.selectedSourceTypeId = null;

      const sourceTypeGroup = this.sequenceMetadataForm.get('sourceType_fields') as FormGroup;
      Object.keys(sourceTypeGroup.controls).forEach((k) => sourceTypeGroup.removeControl(k));
      this.sourceTypeMetadataKeys = [];
      this.updateFormSections();
      this.loadMetadataKeys();
      return;
    }

    const selectedSequence = this.selectedSequence();
    if (selectedSequence) {
      this.drawerState.setSelectedSequence({ ...selectedSequence, source_type: metadataSourceType?.id });
    }

    this.selectedSourceTypeId = metadataSourceType?.id;
    this.loadMetadataKeys(this.selectedSourceTypeId);
  }

  onMultiSelectChange(selectedValues: string[], section: 'core_fields' | 'sourceType_fields', keyName: string): void {
    const formArray = this.getFieldArray(section, keyName);
    if (!formArray) return;

    // Clear previous selection
    formArray.clear();

    // Push new selected values
    if (Array.isArray(selectedValues)) {
      selectedValues.forEach((value) => formArray.push(this.fb.control(value)));
    }
  }

  private getValidatorsForKey(key: { is_required: boolean; data_type: string; name?: string; key_name?: string }) {
    const validators = key.is_required ? [Validators.required] : [];
    if (key.data_type === 'GPS') {
      validators.push(Validators.pattern(GPS_PATTERN));
    }
    return validators;
  }

  /** Lookup the rich schema entry for a given key by name. */
  getFieldSchemaForKey(key: { name?: string; key_name?: string }): IMetadataFieldSchema | null {
    const lookupKeys: string[] = [];
    if (key.key_name) lookupKeys.push(key.key_name);
    if (key.name) lookupKeys.push(key.name);
    for (const k of lookupKeys) {
      const match = this.schemaByName.get(k) ?? this.schemaByName.get(k.toLowerCase());
      if (match) return match;
    }
    return null;
  }

  /** Pull the schema for the currently-selected source type (or core) and
   * index it so the inline requirement hints can render next to each field
   * label. Display-only — the server still owns validation at promotion
   * time. Called whenever the schema arrives, the source type changes, or
   * new key lists are loaded.
   */
  private applySchemaToForm(): void {
    const sourceTypeKey: SchemaCacheKey = this.selectedSourceTypeId ?? 'core';
    this.metadataSchemaService.getSchema(sourceTypeKey).subscribe({
      next: (schema) => {
        this.indexSchema(schema);
      },
      error: (err) => {
        // Hint rendering is non-essential; swallow and log.
        this.logger.warn('Failed to load metadata schema', 'LabSequenceDrawerComponent', err);
      },
    });
  }

  private indexSchema(schema: IMetadataSchema): void {
    this.schemaByName = new Map<string, IMetadataFieldSchema>();
    for (const tpl of schema.templates) {
      if (tpl.name) {
        this.schemaByName.set(tpl.name, tpl);
        this.schemaByName.set(tpl.name.toLowerCase(), tpl);
      }
    }
    this.crossFieldRules = schema.cross_field_rules ?? [];
  }

  removeMultiSelectValue(section: 'core_fields' | 'sourceType_fields', keyName: string, index: number): void {
    const formArray = this.getFieldArray(section, keyName);
    if (formArray) {
      formArray.removeAt(index);
    }
  }

  addListValue(section: 'core_fields' | 'sourceType_fields', keyName: string, inputEl: HTMLInputElement): void {
    const value = inputEl.value.trim();
    if (!value) return;
    const formArray = this.getFieldArray(section, keyName);
    if (!formArray) return;
    const alreadyExists = formArray.controls.some((c) => c.value === value);
    if (!alreadyExists) {
      formArray.push(this.fb.control(value));
      formArray.markAsDirty();
    }
    inputEl.value = '';
  }

  removeListValue(section: 'core_fields' | 'sourceType_fields', keyName: string, index: number): void {
    const formArray = this.getFieldArray(section, keyName);
    if (formArray) {
      formArray.removeAt(index);
      formArray.markAsDirty();
    }
  }

  closeDrawer() {
    // Check if form has unsaved changes (only for non-PRIMARY status)
    if (!this.isReadOnly() && this.hasUnsavedChanges()) {
      // Log dirty fields when unsaved dialog is shown
      const dirtyFields: string[] = [];
      Object.keys(this.sequenceMetadataForm.controls).forEach((key) => {
        const control = this.sequenceMetadataForm.get(key);
        if (control?.dirty) {
          if (control instanceof FormGroup) {
            // For nested form groups, get the dirty fields within
            Object.keys(control.controls).forEach((nestedKey) => {
              if (control.get(nestedKey)?.dirty) {
                dirtyFields.push(`${key}.${nestedKey}`);
              }
            });
          } else {
            dirtyFields.push(key);
          }
        }
      });
      this.logger.debug('Unsaved dialog shown with dirty fields', 'LabSequenceDrawerComponent', dirtyFields);
      this.dialogService.open(UNSAVED_CHANGES_MODAL_ID);
      this.pendingClose = true;
      return;
    }

    this.performClose();
  }

  private performClose() {
    this.drawerState.setSourceTypeId(0);
    this.drawerState.setSelectedSequence(null);
    this.drawerState.setAnalyticalDataset(null);
    this.showAlert.set(true);

    this.sequenceMetadataForm.reset({
      source_type: '',
      description: '',
      gcs_file_path: '',
      core_fields: {},
      sourceType_fields: {},
    });
    this.sequenceMetadataForm.markAsPristine();
    this.sequenceMetadataForm.markAsUntouched();

    this.drawerState.closeDrawer();
    this.drawerState.setAlertMessage({
      message:
        'ALL Required keys must be filled out to convert this sequence into a Primary file—Once completed, this file will be available to be used in analytical datasets.',
      type: 'default',
    });
    this.selectedSourceTypeId = null;
    this.sourceTypeMetadataKeys = [];
    this.coreMetadataKeys = [];
    this.sequenceMetadata = null;
    this.formSections = [];
    // Reset expand/collapse state
    this.isCopiedToExpanded.set(true);
    this.isSequenceDetailsExpanded.set(true);
    this.isSourceTypeExpanded.set(true);
    this.drawerState.setIsOpen(false);
    this.initialFormValue = null;
    this.pendingClose = false;
    this.promotionIssues.set(null);
  }

  private hasUnsavedChanges(): boolean {
    // If no initial value captured yet, no changes
    if (!this.initialFormValue) {
      return false;
    }

    const currentValue = this.sequenceMetadataForm.value;
    return JSON.stringify(this.initialFormValue) !== JSON.stringify(currentValue);
  }

  onSaveAndClose() {
    // Save the metadata first (without promoting to primary)
    this.onSave();
    // The close will happen after successful save via the Actions subscription
    // which resets initialFormValue, so next close won't trigger warning
    this.pendingClose = true;
  }

  onCloseWithoutSaving() {
    this.performClose();
  }

  onContinueEditing() {
    this.pendingClose = false;
  }
  dismissAlert() {
    this.showAlert.set(false);
  }

  collectMetadata() {
    const coreFieldsGroup = this.sequenceMetadataForm.get('core_fields') as FormGroup;
    const sourceTypeFieldsGroup = this.sequenceMetadataForm.get('sourceType_fields') as FormGroup;

    const updateGroupValidity = (group: FormGroup | null) => {
      if (!group) return;
      Object.keys(group.controls).forEach((key) => {
        group.get(key)?.updateValueAndValidity({ emitEvent: false });
      });
    };

    updateGroupValidity(coreFieldsGroup);
    updateGroupValidity(sourceTypeFieldsGroup);

    this.sequenceMetadataForm.updateValueAndValidity({ emitEvent: false });

    const formValue = this.sequenceMetadataForm.value;
    const sequence = this.selectedSequence();

    if (!sequence?.id) {
      this.toastService.show('No sequence selected', 'error');
      return null;
    }

    const metadataTags: KeyValueItem[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processFields = (fields: any, sourceType: number | null) => {
      Object.keys(fields || {}).forEach((key) => {
        const value = fields[key];

        if (value === null || value === undefined || value === '') return;

        const normalizedValue = Array.isArray(value) ? [...value] : String(value);

        metadataTags.push(
          sourceType ? { key, value: normalizedValue, source_type: sourceType } : { key, value: normalizedValue }
        );
      });
    };

    processFields(formValue.core_fields, null);
    processFields(formValue.sourceType_fields, this.selectedSourceTypeId ?? 0);

    return {
      metadataTags,
      sequenceId: sequence.id,
      description: formValue.description || '',
    };
  }

  onSave() {
    const data = this.collectMetadata();
    if (!data) return;

    // Blow away any field-level server errors + banner summary from the last
    // submit so we don't show stale messages; fresh ones (if any) will be
    // reattached by the failure action handler.
    this.clearAllPromotionServerErrors();

    const gcsFilePath = this.sequenceMetadataForm.get('gcs_file_path')?.value;

    this.sequenceMetadataFacade.createSequenceMetadata(
      data.metadataTags,
      data.sequenceId,
      data.description,
      this.selectedSourceTypeId,
      gcsFilePath
    );
  }
  promoteToPrimary() {
    const data = this.collectMetadata();
    if (!data) return;

    // Blow away any field-level server errors + banner summary from the last
    // submit so we don't show stale messages; fresh ones (if any) will be
    // reattached by the failure action handler.
    this.clearAllPromotionServerErrors();

    const gcsFilePath = this.sequenceMetadataForm.get('gcs_file_path')?.value;

    this.sequenceMetadataFacade.promoteToPrimary(
      data.metadataTags,
      data.sequenceId,
      data.description,
      this.selectedSourceTypeId,
      gcsFilePath
    );
  }

  onUploadCSV(): void {
    const selectedSeq = this.selectedSequence();

    if (!selectedSeq) {
      this.toastService.show('No sequence selected', 'error');
      return;
    }

    // Reuse the batch CSV flow scoped to just this sequence. The bulk editor
    // gives per-cell validation, and the file_ids scope means the upload only
    // touches this file even if the CSV happens to contain other rows.
    this.batchCsvUploadState.openWith([selectedSeq]);
  }

  // Open the bulk editor to correct/redact the single open sequence's baseline
  // values. Both correction and redaction live in the editor (batched with
  // Save), so a PRIMARY jumps straight to correction mode and a DRAFT to the
  // ordinary edit flow, scoped to this one file.
  onRedactMetadata(): void {
    if (this.preparingCorrection()) return;
    const selectedSeq = this.selectedSequence();
    if (!selectedSeq) {
      this.toastService.show('No sequence selected', 'error');
      return;
    }
    if (selectedSeq.status === 'PRIMARY') {
      this.batchCsvUploadState.correctDirectly([selectedSeq]);
    } else {
      // Draft -> go straight to the editor when a source type is already set,
      // else the modal opens so the user can pick one.
      this.batchCsvUploadState.editDirectly([selectedSeq]);
    }
  }

  // Refetch the open sequence's header + metadata from the server
  private reloadSelectedSequence(): void {
    const sequenceId = this.selectedSequence()?.id;
    if (!sequenceId) return;

    this.uploadsService.getFileDetail(sequenceId).subscribe({
      next: (sequenceFile) => {
        if (sequenceFile && sequenceFile.id) {
          const currentSequence = this.drawerState.selectedSequence();
          this.drawerState.setSelectedSequence({
            ...sequenceFile,
            created_at: currentSequence?.created_at,
            lab_name: currentSequence?.lab_name,
            // Preserve source_type from current state since API doesn't return it
            source_type: currentSequence?.source_type ?? null,
            source_type_name: currentSequence?.source_type_name ?? null,
          });
          this.drawerState.incrementRefreshSequenceList();
        }
      },
      error: (err) => this.logger.error('Error refreshing file detail', 'LabSequenceDrawerComponent', err),
    });

    this.sequenceMetadataFacade.getSequenceMetadata(sequenceId);
  }

  // Correct/redact is offered wherever the file's baseline metadata is
  // editable, i.e. a DRAFT (edit flow) or a PRIMARY (correction flow).
  canRedact(): boolean {
    const sequence = this.selectedSequence();
    if (!sequence || (sequence.status !== 'PRIMARY' && sequence.status !== 'DRAFT')) return false;
    return this.canChangeSelectedMetadata();
  }

  // Mirrors the backend `can_change_file` permission for the open sequence, and
  // requires the bulk editor host to be mounted (only the lab sequence view has
  // it) so the correct/redact entry points never dead-end from other views.
  private canChangeSelectedMetadata(): boolean {
    if (!this.drawerState.metadataEditorAvailable()) return false;
    if (isLabReadOnly(this.labFacade.getLabDetailSignal())) return false;
    // correcting can only be done from the lab seq list
    if (this.postAnalysisReadOnly()) return false;
    const sequence = this.selectedSequence();
    if (!sequence || this.analyticalDataset()) return false;
    const user = this.meUser();
    if (!user) return false;
    if (user.is_platform_admin) return true;
    const lab = this.labFacade.getLabDetailSignal();
    const isDirector =
      user.lab_memberships?.some(
        (m) => m.id === lab?.id && m.role === 'Lab Director',
      ) ?? false;
    return (
      isDirector ||
      user.id === sequence.created_by ||
      labDraftAccess(user, sequence.lab) === 'EDIT_ALL'
    );
  }

  onPrevSequence(): void {
    this.drawerState.goToPrev();
  }

  onNextSequence(): void {
    this.drawerState.goToNext();
  }

  /**
   * Apply validation errors returned from the backend when promoting a sequence
   * file to PRIMARY. Each issue's `template_keys` is matched against the
   * metadata form controls so the error shows inline with the offending field.
   * Any top-level errors are also surfaced in the banner via `promotionIssues`.
   */
  private applyPromotionValidationErrors(body: PromotionErrorBody): void {
    const backendErrors = Array.isArray(body.errors) ? body.errors : [];
    const backendWarnings = Array.isArray(body.warnings) ? body.warnings : [];
    const missingKeyNames = Array.isArray(body.missing_metadata_keys) ? body.missing_metadata_keys : [];

    if (backendErrors.length === 0 && backendWarnings.length === 0 && missingKeyNames.length === 0) {
      return;
    }

    // Attach each issue to the matching form control(s). Warnings and errors
    // use the same storage slot — if a field has both, the error wins because
    // it's applied last.
    backendWarnings.forEach((issue) => this.attachIssueToFields(issue));
    backendErrors.forEach((issue) => this.attachIssueToFields(issue));

    // Missing-required keys come back separately (not as `errors`) so populate
    // them as a REQUIRED-style server error on the matching controls.
    if (missingKeyNames.length > 0) {
      this.markBackendMissingKeysInvalid(missingKeyNames);
    }

    this.promotionIssues.set({
      errors: backendErrors,
      warnings: backendWarnings,
      missingRequiredKeys: missingKeyNames,
    });

    // Surface a concise summary in the alert banner.
    const errorCount = backendErrors.length + missingKeyNames.length;
    const warningCount = backendWarnings.length;
    const bannerMessage = body.detail
      || (errorCount > 0
        ? `This sequence cannot be promoted to PRIMARY: ${errorCount} error${errorCount === 1 ? '' : 's'} must be resolved.`
        : `This sequence has ${warningCount} warning${warningCount === 1 ? '' : 's'} that must be acknowledged before promotion.`);

    this.drawerState.setAlertMessage({
      message: bannerMessage,
      type: errorCount > 0 ? 'error' : 'warning',
    });
    this.showAlert.set(true);
  }

  /**
   * Look up the form control corresponding to each template_key on an issue
   * and pin the issue's message onto it as a `serverError`. Controls are
   * located by matching the backend key name against either `name` or
   * `key_name` on the metadata key definitions.
   */
  private attachIssueToFields(issue: PromotionValidationIssue): void {
    const keys = Array.isArray(issue.template_keys) ? issue.template_keys : [];
    keys.forEach((keyName) => {
      const { section, controlName } = this.locateFieldByKeyName(keyName);
      if (!section || !controlName) return;

      const control =
        this.getFieldControl(section, controlName) ?? this.getFieldArray(section, controlName);
      if (!control) return;

      const existingErrors = control.errors ? { ...control.errors } : {};
      existingErrors['serverError'] = issue.message;
      control.setErrors(existingErrors);
      control.markAsTouched();
    });
  }

  /**
   * Resolve which section (core vs source-type) a backend key name lives in
   * and return the form control name used for that key. Returns nullish
   * members if no matching metadata key definition is found.
   */
  private locateFieldByKeyName(
    keyName: string
  ): { section: 'core_fields' | 'sourceType_fields' | null; controlName: string | null } {
    const matches = (k: IMetadataKeyList) => k.name === keyName || k.key_name === keyName;

    const coreKey = this.coreMetadataKeys.find(matches);
    if (coreKey) {
      return { section: 'core_fields', controlName: coreKey.key_name || coreKey.name };
    }
    const sourceKey = this.sourceTypeMetadataKeys.find(matches);
    if (sourceKey) {
      return { section: 'sourceType_fields', controlName: sourceKey.key_name || sourceKey.name };
    }
    return { section: null, controlName: null };
  }

  private markBackendMissingKeysInvalid(missingKeyNames: string[]): void {
    missingKeyNames.forEach((keyName) => {
      const { section, controlName } = this.locateFieldByKeyName(keyName);
      if (!section || !controlName) return;

      const control =
        this.getFieldControl(section, controlName) ?? this.getFieldArray(section, controlName);
      if (!control) return;

      const existingErrors = control.errors ? { ...control.errors } : {};
      existingErrors['serverError'] = 'This field is required.';
      existingErrors['required'] = true;
      control.setErrors(existingErrors);
      control.markAsTouched();
    });
  }

  /**
   * Clear the `serverError` previously pinned onto a form control. Invoked
   * from the template when the user edits a field that carried a validation
   * error from a prior promote attempt, so the UI doesn't show a stale error
   * while they're actively correcting the value.
   */
  /** Template helper: pull the serverError message off a metadata field, if any. */
  getFieldServerError(section: 'core_fields' | 'sourceType_fields', keyName: string): string | null {
    const control = this.getFieldControl(section, keyName) ?? this.getFieldArray(section, keyName);
    const msg = control?.errors?.['serverError'];
    return typeof msg === 'string' ? msg : null;
  }

  /**
   * Wipe every `serverError` pinned onto the metadata form controls and clear
   * the banner summary. Called immediately before a new save / promote
   * request so the user sees a clean slate; whatever the backend reports on
   * the next response will be freshly reattached. Between submits we *keep*
   * stale errors on purpose so the user can see the full list of issues to
   * fix across multiple fields.
   */
  private clearAllPromotionServerErrors(): void {
    (['core_fields', 'sourceType_fields'] as const).forEach((sectionKey) => {
      const group = this.sequenceMetadataForm.get(sectionKey) as FormGroup | null;
      if (!group) return;
      Object.keys(group.controls).forEach((controlName) => {
        const ctrl = group.get(controlName);
        if (!ctrl?.errors?.['serverError']) return;
        // setErrors(null) discards the manually-attached serverError/required;
        // updateValueAndValidity re-runs the control's real validators so
        // required/pattern/etc. reappear if the current value is still bad.
        ctrl.setErrors(null);
        ctrl.updateValueAndValidity({ emitEvent: false });
      });
    });
    this.promotionIssues.set(null);
  }

  private getFormGroupErrors(formGroup: FormGroup): string[] {
    const errors: string[] = [];
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control && control.invalid) {
        if (control instanceof FormArray) {
          if (control.errors) {
            errors.push(`${key} (FormArray): ${JSON.stringify(control.errors)}`);
          }
          // Check each control in the array
          control.controls.forEach((arrayControl, index) => {
            if (arrayControl.invalid) {
              errors.push(`${key}[${index}]: ${JSON.stringify(arrayControl.errors)}`);
            }
          });
        } else {
          errors.push(`${key}: ${JSON.stringify(control.errors)}`);
        }
      }
    });
    return errors;
  }
  addYears(date: string | Date | undefined | null, years: number = 5): Date | null {
    if (!date) return null; // handles undefined & null
    const d = new Date(date);
    d.setFullYear(d.getFullYear() + years);
    return d;
  }

  getFields(sectionKey: 'core_fields' | 'sourceType_fields') {
    const data: MetadataTag[] = this.analyticalDataset()?.metadata_tags ?? this.sequenceMetadata?.metadata_tags ?? [];

    const shouldInclude = (item: MetadataTag) =>
      sectionKey === 'sourceType_fields' ? item.source_type != null : item.source_type === null;

    return data
      .filter(shouldInclude)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((item: MetadataTag) => {
        let value = item.value;

        // For DATE fields, use only the first value from array
        if (item.data_type === 'DATE' && Array.isArray(value)) {
          value = value[0] || '';
        } else if (Array.isArray(value)) {
          value = value.join(', ');
        }

        return {
          key: item.key,
          is_required: item.is_required,
          value,
          data_type: item.data_type,
          corrections: item.corrections ?? [],
          is_redacted: item.is_redacted ?? false,
        };
      });
  }

  // fixed pos tooltip for the corrections history
  hoverCorrection = signal<{ corrections: MetadataTagCorrection[]; top: number; right: number } | null>(null);

  showCorrectionTooltip(event: MouseEvent, corrections: MetadataTagCorrection[]): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.hoverCorrection.set({
      corrections,
      top: rect.top + rect.height / 2,
      // Anchor the tooltip's right edge just left of the badge, grows leftward
      right: window.innerWidth - rect.left + 8,
    });
  }

  hideCorrectionTooltip(): void {
    this.hoverCorrection.set(null);
  }

  // fixed pos tooltip explaining what a redacted field means, positioned like
  // the corrections tooltip
  hoverRedaction = signal<{ top: number; right: number } | null>(null);

  showRedactionTooltip(event: MouseEvent): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.hoverRedaction.set({
      top: rect.top + rect.height / 2,
      right: window.innerWidth - rect.left + 8,
    });
  }

  hideRedactionTooltip(): void {
    this.hoverRedaction.set(null);
  }

  // Convert date from various formats to ISO format (YYYY-MM-DD) for HTML5 date input
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private convertToISODate(dateValue: any): string {
    // If an array is passed, use the first value (handle nested arrays too)
    let dateString = dateValue;
    while (Array.isArray(dateString)) {
      dateString = dateString[0];
    }

    if (!dateString || typeof dateString !== 'string') return '';

    // If already in ISO format (YYYY-MM-DD), return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Try to parse M/D/YYYY or MM/DD/YYYY format
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }

    // If we can't parse it, try using Date object as fallback
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error('Failed to parse date:', dateString, e);
    }

    return dateString; // Return original if all parsing fails
  }

  // Helper method to safely format a date for display
  formatDateForDisplay(value: string | null | undefined): string {
    if (!value || typeof value !== 'string') return '-';

    // Check if it's a valid date
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return '-';

      // Format as MM/dd/yyyy
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch {
      return '-';
    }
  }

  // Helper method to get source type name
  getSourceTypeName(): string {
    if (this.analyticalDataset()) {
      const sourceType = this.sourceTypes.find((st) => st.id === this.drawerState.selectedSequenceId()?.source_type);
      return sourceType?.name || '';
    }
    if (!this.selectedSourceTypeId) return '';
    const sourceType = this.sourceTypes.find((st) => st.id === this.selectedSourceTypeId);
    return sourceType?.name || '';
  }

  isPublicStatus(): boolean {
    const status = this.selectedSequence()?.status || this.analyticalDataset()?.status;
    if (!status) return false;
    return ['UPLOADED', 'DELETED', 'ARCHIVED', 'PRIMARY'].includes(status);
  }

  // True when the open sequence is a PRIMARY file with an archive request still
  // awaiting Lab Director approval.
  isPendingArchive(): boolean {
    const sequence = this.selectedSequence();
    return !this.analyticalDataset() && sequence?.status === 'PRIMARY' && !!sequence?.has_pending_archive_request;
  }

  // Status label shown in the drawer header (surfaces "Pending Archive" for a
  // PRIMARY file with an outstanding archive request).
  displayStatus(): string {
    if (this.analyticalDataset()) {
      return this.analyticalDataset()!.status || 'Unknown';
    }
    if (this.isPendingArchive()) {
      return 'PENDING ARCHIVE';
    }
    return this.selectedSequence()?.status || 'Unknown';
  }

  // only DRAFT files the user is allowed to edit are editable
  isReadOnly(): boolean {
    // A read only lab stays read only regardless of file status.
    if (isLabReadOnly(this.labFacade.getLabDetailSignal())) return true;
    const sequence = this.selectedSequence();
    const status = sequence?.status || this.analyticalDataset()?.status;
    if (!status) return true;
    if (status !== 'DRAFT') return true;
    // A draft is read-only for members who can view but not edit it (VIEW_ALL).
    // Datasets have no sequence here, so this only applies to the sequence case.
    return !!sequence && !canEditDraft(this.meUser(), sequence);
  }

  onViewDataset(item: CopiedToDataset) {
    const labId = item.lab_id;
    const projectId = item.project_id;
    const analyticalDatasetId = item.analytical_dataset_id;

    if (!labId || !projectId) {
      this.toastService.show('Lab ID or Project ID is not available', 'error');
      return;
    }

    // Build query parameters
    const queryParams: { tab: string; analyticalDatasetId?: number } = { tab: 'datasets' };
    if (analyticalDatasetId) {
      queryParams.analyticalDatasetId = analyticalDatasetId;
    }
    // Navigate to project details page with datasets tab selected and analytical dataset ID if available
    this.router.navigate(['/labs', labId, 'project', projectId], { queryParams });
    this.closeDrawer();
  }

  onExportCSV() {
    let sequenceId: number | undefined;

    if (this.analyticalDataset()?.id) {
      sequenceId = this.analyticalDataset()!.id;
    } else if (this.drawerStateIsOpen()) {
      sequenceId = this.drawerState.selectedSequenceId()?.id;
    } else {
      sequenceId = this.selectedSequence()?.id;
    }
    if (!sequenceId) {
      this.toastService.show('Sequence ID is not available for CSV export', 'error');
      return;
    }

    this.uploadsService.exportCSV(sequenceId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'csv_template.csv';
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastService.show('Download failed', 'error');
      },
    });
  }

  toggleCopiedToSection() {
    this.isCopiedToExpanded.update((value) => !value);
  }

  toggleSequenceDetailsSection() {
    this.isSequenceDetailsExpanded.update((value) => !value);
  }

  toggleSourceTypeSection() {
    this.isSourceTypeExpanded.update((value) => !value);
  }
}
